import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

dayjs.tz.setDefault('America/Los_Angeles');

export function formatPstDateTime(dateString: string, shortDay: true): string {
  const d = dayjs(dateString);
  const dayOfWeek = shortDay ? d.format('ddd') : d.format('dddd');
  const month = d.format('MMM');
  const dayNum = d.format('D');
  const time = d.format('h:mm a');
  // PST should be on the same line as the time
  return `${dayOfWeek}, ${month} ${dayNum}, ${time} PST`;
}

export function getPstNow(): string {
  return dayjs().tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss');
}

export function getEpochNow(): number {
  return dayjs().unix();
}

export function getPstTime(timestamp: string | number | Date): string {
  return dayjs(timestamp).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss');
}

export function getUtcTime(timestamp: string | number | Date): string {
  return dayjs(timestamp).tz('UTC').format('YYYY-MM-DD HH:mm:ss');
}

export function addTime(timestamp: string | number | Date, amount: number, unit: dayjs.ManipulateType): string {
  // round up to the next hour
  const roundedUpHour = dayjs(timestamp).tz('America/Los_Angeles').add(1, 'hour').startOf('hour');
  // add the amount and unit
  const newTime = roundedUpHour.add(amount, unit);
  return newTime.format('YYYY-MM-DD HH:mm:ss');
}

export function subtractTime(timestamp: string | number | Date, amount: number, unit: dayjs.ManipulateType): string {
  return dayjs(timestamp).subtract(amount, unit).format('YYYY-MM-DD HH:mm:ss');
}

export function getThisMidnight(timestamp: string | number | Date): string {
  // returns 11:59:59 PM of the current day
  return dayjs(timestamp).tz('America/Los_Angeles').endOf('day').format('YYYY-MM-DD HH:mm:ss');
} 


export function displayTimeStamp(timestamp: string | number | Date): string {
  // let's see if today or yesterday:
  const today = dayjs().tz('America/Los_Angeles');
  const yesterday = today.subtract(1, 'day');
  const timestampDate = dayjs(timestamp).tz('America/Los_Angeles');
  if (timestampDate.isSame(today, 'day')) {
    return `${timestampDate.format('h:mm A')} PST`;
  } else if (timestampDate.isSame(yesterday, 'day')) {
    return `Yesterday ${timestampDate.format('h:mm A')} PST`;
  } else {
    return`${timestampDate.format('MM/DD/YYYY hh:mm A')} PST`;
  }
}

/**
 * Returns minutes until timestamp (positive = future, negative = past).
 * Expiry is always interpreted in PST so EST (and other) users see the same
 * "accepting offers until" / "expired" state as PST users.
 */
export function getPstTimeDifference(timestamp: string | number | Date): number {
  const now = dayjs().tz('America/Los_Angeles');
  const raw = timestamp == null ? '' : String(timestamp);
  // If the value has no timezone (no Z, no +/-offset), parse it as PST so
  // EST users don't see "expired" 3 hours early.
  const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(raw.trim());
  const timestampDate = hasZone
    ? dayjs(timestamp)
    : dayjs.tz(raw.replace('T', ' ').substring(0, 19), 'America/Los_Angeles');
  const diff = timestampDate.diff(now, 'minutes');
  return diff;
}

export function getExpiryCountdown(expiresAt: string | number | Date): string {

  const now = dayjs().tz('America/Los_Angeles');

  // Parse the expiry time as PST time (not UTC)
  const plusAt = String(expiresAt).indexOf('+');
  const expiresAtPst = plusAt > 0 ? String(expiresAt).substring(0, plusAt) : String(expiresAt);
  // Parse the string as if it represents a time in PST timezone
  const expiry = dayjs.tz(expiresAtPst, 'America/Los_Angeles');
  console.log('now, expiry:', now.format('YYYY-MM-DD HH:mm:ss'), expiry.format('YYYY-MM-DD HH:mm:ss'));
  console.log('now tz:', now.format('Z'), 'expiry tz:', expiry.format('Z'));
  console.log('diffMinutes:', expiry.diff(now, 'minute'));
  
  // Calculate time difference in minutes
  const diffMinutes = expiry.diff(now, 'minute');
  // If expired or invalid date
  if (diffMinutes <= 0 || !expiry.isValid()) {
    const expiredDate = expiry.format('MM/DD/YYYY');
    const expiredTime = expiry.format('h:mm A');
    return `<span style="color: #dd6b20;">Expired on ${expiredDate} at ${expiredTime}</span>`;
  }
  
  // Less than an hour: "in X Minutes" - RED (urgent)
  if (diffMinutes < 60) {
    return `<span style="color: #e53e3e;">Expires in ${diffMinutes} Minutes</span>`;
  }
  
  // Check if it expires at midnight (11:59 PM) - ORANGE (medium urgency)
  const midnight = now.endOf('day');
  if (expiry.isSame(midnight, 'minute')) {
    return '<span style="color: #dd6b20;">Expires at midnight PST</span>';
  }
  
  // More than 59 minutes but before midnight tonight - ORANGE (medium urgency)
  if (diffMinutes < 1440 && expiry.isSame(now, 'day')) { // 1440 = 24 * 60 minutes
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;
    return `<span style="color: #dd6b20;">Expires in ${hours} hours and ${minutes} minutes</span>`;
  }
  
  // After midnight but during tomorrow - ORANGE (medium urgency)
  const tomorrow = now.add(1, 'day');
  const daysDiff = expiry.diff(now, 'day');
  if (expiry.isSame(tomorrow, 'day') || daysDiff === 1) {
    const hour = expiry.format('h');
    const minute = expiry.format('mm');
    const ampm = expiry.format('A');
    return `<span style="color: #dd6b20;">Expires tomorrow at ${hour}:${minute} ${ampm} PST</span>`;
  }
  
  // After tomorrow: "in X days at Y:Z PST" - NO COLOR (inherits from CSS)
  const hour = expiry.format('h');
  const minute = expiry.format('mm');
  const ampm = expiry.format('A');
  return `Expires in ${daysDiff} days at ${hour}:${minute} ${ampm} PST`;
}